import React, {useMemo, useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import {SlidersHorizontal, ChevronDown, ChevronUp, LayoutDashboard, RefreshCw} from "lucide-react";
import {useTranslation} from "react-i18next";
import useKpiPreferences from "./useKpiPreferences";
import usePullToRefresh from "./usePullToRefresh";
import {useActivitySummary, usePerformanceByHour, useCustomerRetention} from "./useActivityAnalytics";
import {KPI_BY_ID, CHART_BY_ID} from "./widgetCatalog";
import {CHART_COMPONENTS} from "./ChartWidgets";
import KpiCard from "./KpiCard";
import KpiSelectorPanel from "./KpiSelectorPanel";

/**
 * Activity analytics dashboard. Renders only the KPI cards / charts the user has
 * opted into, and only fetches an API when at least one visible widget needs it.
 *
 * Designed to sit at the top of the "Résumé" tab, above the existing widgets.
 */
const AnalyticsDashboard = ({dateRange, startTime, endTime, isDarkMode = false, refreshKey = 0, onRefresh, comparisonRange = null}) => {
    const {t} = useTranslation("activite");
    const prefs = useKpiPreferences();
    const [showConfig, setShowConfig] = useState(false);

    const {pull, refreshing} = usePullToRefresh(onRefresh);

    const {visibleKpis, visibleCharts} = prefs;

    // Decide which APIs are actually required by the currently visible widgets.
    const {needSummary, needPerformance, needRetention} = useMemo(() => {
        const sources = new Set();
        visibleKpis.forEach((id) => KPI_BY_ID[id] && sources.add(KPI_BY_ID[id].dataSource));
        visibleCharts.forEach((id) => CHART_BY_ID[id] && sources.add(CHART_BY_ID[id].dataSource));
        return {
            needSummary: sources.has("summary"),
            needPerformance: sources.has("performance"),
            needRetention: sources.has("retention"),
        };
    }, [visibleKpis, visibleCharts]);

    const summaryQuery = useActivitySummary(dateRange, startTime, endTime, refreshKey, needSummary);
    const performanceQuery = usePerformanceByHour(dateRange, startTime, endTime, refreshKey, needPerformance);
    const retentionQuery = useCustomerRetention(dateRange, startTime, endTime, refreshKey, needRetention);

    // Same queries for the comparison period — only when comparison is on.
    const comparing = !!comparisonRange;
    const prevSummaryQuery = useActivitySummary(comparisonRange, startTime, endTime, refreshKey, comparing && needSummary);
    const prevPerformanceQuery = usePerformanceByHour(comparisonRange, startTime, endTime, refreshKey, comparing && needPerformance);
    const prevRetentionQuery = useCustomerRetention(comparisonRange, startTime, endTime, refreshKey, comparing && needRetention);
    const compareDataBySource = comparing
        ? {summary: prevSummaryQuery.data, performance: prevPerformanceQuery.data, retention: prevRetentionQuery.data}
        : {};
    // What the charts need to draw the comparison series. Memoized per source
    // so the charts' row building only re-runs when that data changes.
    const prevSummaryData = prevSummaryQuery.data;
    const prevPerformanceData = prevPerformanceQuery.data;
    const prevRetentionData = prevRetentionQuery.data;
    const chartComparisonBySource = useMemo(() => {
        if (!comparing) return {};
        const build = (data) => ({data, currentRange: dateRange, previousRange: comparisonRange});
        return {
            summary: build(prevSummaryData),
            performance: build(prevPerformanceData),
            retention: build(prevRetentionData),
        };
    }, [comparing, dateRange, comparisonRange, prevSummaryData, prevPerformanceData, prevRetentionData]);

    // A widget stays in its loading state until the comparison period (when
    // on) has loaded too, so it never flashes a flat "0" comparison series.
    const queryBySource = {
        summary: {
            data: summaryQuery.data,
            isLoading: needSummary && (summaryQuery.isLoading || (comparing && prevSummaryQuery.isLoading)),
        },
        performance: {
            data: performanceQuery.data,
            isLoading: needPerformance && (performanceQuery.isLoading || (comparing && prevPerformanceQuery.isLoading)),
        },
        retention: {
            data: retentionQuery.data,
            isLoading: needRetention && (retentionQuery.isLoading || (comparing && prevRetentionQuery.isLoading)),
        },
    };

    const dataFor = (source) => queryBySource[source]?.data;
    const loadingFor = (source) => queryBySource[source]?.isLoading;

    const hasAnyWidget = visibleKpis.length > 0 || visibleCharts.length > 0;

    // Wait for stored preferences before deciding what to render (avoids a flash).
    if (!prefs.ready) {
        return (
            <div className="grid grid-cols-2 gap-3 px-4 pt-4 md:grid-cols-4">
                {Array.from({length: 4}).map((_, i) => (
                    <div
                        key={i}
                        className={`h-24 animate-pulse rounded-xl ${
                            isDarkMode ? "bg-gray-800" : "bg-gray-100"
                        }`}
                    />
                ))}
            </div>
        );
    }

    return (
        <section className="px-4 pt-4">
            {/* Pull-to-refresh indicator */}
            {(pull > 0 || refreshing) && (
                <div
                    className="flex items-center justify-center overflow-hidden"
                    style={{height: refreshing ? 40 : pull}}
                >
                    <RefreshCw
                        className={`h-5 w-5 text-primary ${refreshing ? "animate-spin" : ""}`}
                        style={{transform: refreshing ? undefined : `rotate(${pull * 3}deg)`}}
                    />
                </div>
            )}

            {/* Section header */}
            <div className="mb-3 flex items-center justify-between">
                <h2
                    className={`flex items-center gap-2 text-base font-semibold ${
                        isDarkMode ? "text-gray-100" : "text-gray-800"
                    }`}
                >
                    <LayoutDashboard className="h-5 w-5 text-primary" />
                    {t("dashboard.title")}
                </h2>
                <button
                    type="button"
                    onClick={() => setShowConfig((s) => !s)}
                    className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                        showConfig
                            ? "border-primary bg-primary/10 text-primary"
                            : isDarkMode
                                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                                : "border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                    {t("dashboard.moreIndicators")}
                    {showConfig ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                    ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                    )}
                </button>
            </div>

            {/* Empty state when nothing is enabled */}
            {!hasAnyWidget && !showConfig && (
                <div
                    className={`flex flex-col items-center justify-center rounded-xl border border-dashed py-10 text-center ${
                        isDarkMode ? "border-gray-700 bg-gray-800/40" : "border-gray-300 bg-gray-50"
                    }`}
                >
                    <LayoutDashboard
                        className={`mb-2 h-8 w-8 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
                    />
                    <p className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                        {t("dashboard.noWidgetsShown")}
                    </p>
                    <p className={`mb-4 max-w-sm text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                        {t("dashboard.enableWidgetsHint")}
                    </p>
                    <button
                        type="button"
                        onClick={() => setShowConfig(true)}
                        className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-medium text-white transition-opacity hover:opacity-90"
                    >
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                        {t("dashboard.moreIndicators")}
                    </button>
                </div>
            )}

            {/* KPI cards */}
            {visibleKpis.length > 0 && (
                <motion.div
                    layout
                    className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
                >
                    <AnimatePresence mode="popLayout">
                        {visibleKpis.map((id) => {
                            const def = KPI_BY_ID[id];
                            if (!def) return null;
                            return (
                                <KpiCard
                                    key={id}
                                    definition={def}
                                    data={dataFor(def.dataSource)}
                                    isLoading={loadingFor(def.dataSource)}
                                    isDarkMode={isDarkMode}
                                    compareData={compareDataBySource[def.dataSource]}
                                />
                            );
                        })}
                    </AnimatePresence>
                </motion.div>
            )}

            {/* Charts */}
            {visibleCharts.length > 0 && (
                <motion.div layout className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <AnimatePresence mode="popLayout">
                        {visibleCharts.map((id) => {
                            const def = CHART_BY_ID[id];
                            const ChartComponent = CHART_COMPONENTS[id];
                            if (!def || !ChartComponent) return null;
                            return (
                                <motion.div
                                    key={id}
                                    layout
                                    className={def.span === 2 ? "lg:col-span-2" : "lg:col-span-1"}
                                >
                                    <ChartComponent
                                        data={dataFor(def.dataSource)}
                                        isLoading={loadingFor(def.dataSource)}
                                        isDarkMode={isDarkMode}
                                        comparison={chartComparisonBySource[def.dataSource]}
                                    />
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </motion.div>
            )}

            {/* Configuration / KPI selector (modal) */}
            {showConfig && (
                <KpiSelectorPanel
                    prefs={prefs}
                    isDarkMode={isDarkMode}
                    onClose={() => setShowConfig(false)}
                />
            )}
        </section>
    );
};

export default AnalyticsDashboard;
